import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['All', 'Plot', 'House', 'Commercial'];
const locations = ['All', 'Shivpuri', 'Jamul', 'Bhilai', 'Khedamara', 'Other'];
const statuses = ['All', 'Available', 'Booked', 'Sold', 'Coming Soon'];

const statusColors = {
  'Available': 'bg-green-100 text-green-800',
  'Booked': 'bg-yellow-100 text-yellow-800',
  'Sold': 'bg-red-100 text-red-800',
  'Coming Soon': 'bg-blue-100 text-blue-800',
};

export default function PropertiesList() {
  const { token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: 'All', location: 'All', status: 'All', search: '' });
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/properties/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(properties.filter(p => p.id !== deleteId));
      toast.success('Property deleted successfully');
    } catch (error) {
      toast.error('Failed to delete property');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const filteredProperties = properties.filter(prop => {
    if (filters.category !== 'All' && prop.category !== filters.category) return false;
    if (filters.location !== 'All' && prop.location_label !== filters.location) return false;
    if (filters.status !== 'All' && prop.status !== filters.status) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        prop.title.toLowerCase().includes(searchLower) ||
        prop.code.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const formatPrice = (prop) => {
    const price = prop.total_price || (prop.rate_per_sqft && (prop.plot_area_sqft || prop.house_area_sqft) 
      ? prop.rate_per_sqft * (prop.plot_area_sqft || prop.house_area_sqft) : null);
    if (!price) return prop.rate_per_sqft ? `₹${prop.rate_per_sqft}/sq ft` : '-';
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString('en-IN')}`;
  };

  return (
    <div data-testid="properties-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">All Properties</h1>
          <p className="text-gray-500">Manage your property listings</p>
        </div>
        <Link to="/dashboard/properties/new">
          <Button className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="add-property-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Property
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by title or code..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger className="w-full md:w-40" data-testid="category-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat === 'All' ? 'All Categories' : cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.location} onValueChange={(v) => setFilters({ ...filters, location: v })}>
              <SelectTrigger className="w-full md:w-40" data-testid="location-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc} value={loc}>{loc === 'All' ? 'All Locations' : loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((stat) => (
                  <SelectItem key={stat} value={stat}>{stat === 'All' ? 'All Status' : stat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Properties Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
            </div>
          ) : filteredProperties.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th className="text-left p-4">Code</th>
                    <th className="text-left p-4">Title</th>
                    <th className="text-left p-4">Category</th>
                    <th className="text-left p-4">Location</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Price/Rate</th>
                    <th className="text-left p-4">Featured</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProperties.map((prop) => (
                    <tr key={prop.id} className="border-t" data-testid={`property-row-${prop.code}`}>
                      <td className="p-4">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{prop.code}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-3">
                          {prop.main_image && (
                            <img src={prop.main_image} alt="" className="w-10 h-10 rounded object-cover" />
                          )}
                          <span className="font-medium">{prop.title}</span>
                        </div>
                      </td>
                      <td className="p-4">{prop.category}</td>
                      <td className="p-4">{prop.location_label}</td>
                      <td className="p-4">
                        <Badge className={statusColors[prop.status]}>{prop.status}</Badge>
                      </td>
                      <td className="p-4">{formatPrice(prop)}</td>
                      <td className="p-4">
                        {prop.featured ? (
                          <Badge className="bg-brand-gold text-gray-900">Yes</Badge>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <Link to={`/dashboard/properties/${prop.id}/edit`}>
                            <Button variant="outline" size="sm" data-testid={`edit-btn-${prop.code}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteId(prop.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-btn-${prop.code}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-500 mb-4">No properties found</p>
              <Link to="/dashboard/properties/new">
                <Button variant="outline">Add Your First Property</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span>Delete Property</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this property? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
